import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BarChartIcon from "@mui/icons-material/BarChart";
import EmailIcon from "@mui/icons-material/Email";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TableViewIcon from "@mui/icons-material/TableView";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

export type NavigationRoute = {
  title: string | ((_props: any) => string);
  Icon?: any;
  route: string;
  description?: string;
  isDisabled?: (_props?: any) => boolean;
};

export type NavigationSection = Array<NavigationRoute> | { grow: true; route: undefined; title: undefined };

export type NavigationContext = {
  roles: string[];
  groups: string[];
  isSelectedPPT?: boolean;
  showCareProvidersForPPT: boolean;
  withProviderRegistry?: boolean;
  withProviderDirectory?: boolean;
  withMessages?: boolean;
  withReports?: boolean;
  getParticipantLabel?: () => string;
  isParticipantSelected?: () => boolean;
};

const grow: NavigationSection = { grow: true, route: undefined, title: undefined };

const profileAndLogout: NavigationRoute[] = [
  {
    title: "Profile",
    Icon: AccountCircleIcon,
    route: "/profile",
  },
  {
    title: "Logout",
    Icon: LogoutIcon,
    route: "/logout",
  },
];

const messageCenter: NavigationRoute = {
  title: "Message Center",
  Icon: EmailIcon,
  route: "/message-center",
  description: "Access your messages and communications.",
};

const manageUsers: NavigationRoute = {
  title: "Manage Users",
  Icon: ManageAccountsIcon,
  route: "/manage-users",
  description: "Manage user accounts and permissions",
};

const navigationLoggedIn: NavigationSection[] = [grow, profileAndLogout];

const getNavigationMapPPT = (isSelectedPPT: boolean, showCareProviders: boolean): NavigationSection[] => [
  [
    {
      title: "Dashboard",
      Icon: isSelectedPPT ? BarChartIcon : HomeIcon,
      route: "/",
      description: "Dashboard overview for participant progress and care activity.",
    },
    {
      title: "Detail",
      Icon: TableViewIcon,
      route: "/budget-data",
      description: "View budget details and authorization information for your services.",
    },
    ...(isSelectedPPT ? [] : [messageCenter]),
    {
      title: "Service Providers",
      Icon: GroupsIcon,
      route: "/providers",
      description: "Search and manage service providers serving your care network.",
    },
    ...(!!isSelectedPPT || !showCareProviders
      ? []
      : [
          {
            title: "Provider Directory",
            Icon: TravelExploreIcon,
            route: "/careproviders",
            description: "Access the provider directory and service provider details.",
          },
        ]),
  ],
  grow,
  profileAndLogout,
];

const navigationMapProvider: NavigationSection[] = [
  [
    {
      title: "Home",
      Icon: HomeIcon,
      route: "/",
      description: "Provider portal homepage for account overview and quick access.",
    },
    messageCenter,
  ],
  grow,
  profileAndLogout,
];

const getNavigationMapCaseManager = (getParticipantLabel?: () => string, isParticipantSelected?: () => boolean): NavigationSection[] => [
  [
    {
      title: "Home",
      Icon: HomeIcon,
      route: "/",
      description: "Staff portal homepage for participant care and provider workflows.",
    },
    messageCenter,
    {
      title: getParticipantLabel ?? "Participant/Member",
      Icon: QueryStatsIcon,
      route: "/member",
      description: "Review participant/member-specific details and care planning information.",
      isDisabled: isParticipantSelected ? () => !isParticipantSelected() : () => true,
    },
    {
      title: "Provider Registry",
      Icon: GroupsIcon,
      route: "/registry",
      description: "Browse the provider registry and locate care providers.",
    },
    {
      title: "Provider Directory",
      Icon: TravelExploreIcon,
      route: "/careproviders",
      description: "Access the provider directory and service provider details.",
    },
    {
      title: "Reports",
      Icon: AssessmentIcon,
      route: "/reports",
      description: "View participant/member usage reports.",
    },
  ],
  grow,
  profileAndLogout,
];

export const getNavigationMap = ({
  roles,
  groups,
  isSelectedPPT = false,
  showCareProvidersForPPT = false,
  withProviderRegistry = true,
  withProviderDirectory = true,
  withMessages = true,
  withReports = true,
  getParticipantLabel,
  isParticipantSelected,
}: NavigationContext): NavigationSection[] => {
  let navigationMap: NavigationSection[];

  if (isSelectedPPT || roles.includes("participant")) {
    navigationMap = getNavigationMapPPT(isSelectedPPT, showCareProvidersForPPT);
  } else if (roles.includes("provider")) {
    navigationMap = navigationMapProvider;
  } else if (["case_manager", "program_admin", "global_admin"].some((role) => roles.includes(role))) {
    navigationMap = getNavigationMapCaseManager(getParticipantLabel, isParticipantSelected);
  } else {
    navigationMap = navigationLoggedIn;
  }

  if (["/customer-portal-user-admins", "/customer-portal-user-staff"].some((group) => groups.includes(group))) {
    if (!(navigationMap.at(-1) as NavigationRoute[])?.some((item) => item.route === manageUsers.route)) {
      (navigationMap.at(-1) as NavigationRoute[])?.unshift(manageUsers);
    }
  }

  if (!withProviderRegistry) {
    navigationMap = navigationMap.map((section) =>
      Array.isArray(section) ? section.filter((item) => item.route !== "/registry") : section,
    );
  }

  if (!withProviderDirectory) {
    navigationMap = navigationMap.map((section) =>
      Array.isArray(section) ? section.filter((item) => item.route !== "/careproviders") : section,
    );
  }

  if (!withMessages) {
    navigationMap = navigationMap.map((section) =>
      Array.isArray(section) ? section.filter((item) => item.route !== "/message-center") : section,
    );
  }

  if (!withReports) {
    navigationMap = navigationMap.map((section) =>
      Array.isArray(section) ? section.filter((item) => item.route !== "/reports") : section,
    );
  }

  return navigationMap;
};

export const unauthorizedNavigationMap: NavigationRoute[] = [
  {
    title: "Login",
    route: "/",
    description: "Login to the portal or select an action to continue.",
  },
  {
    title: "Complete Registration",
    route: "/complete-registration",
    description: "Complete your provider registration to access the portal.",
  },
  {
    title: "Register",
    route: "/register",
    description: "Register for access to the ASD customer portal.",
  },
  {
    title: "Forgot password",
    route: "/forgot-password",
    description: "Reset your portal password and regain account access.",
  },
  {
    title: "Reset Password",
    route: "/reset-password",
    description: "Reset your password to sign in to the provider portal.",
  },
];

export const getNavigationTitle = ({
  roles,
  groups,
  isSelectedPPT,
  showCareProvidersForPPT,
  withProviderRegistry,
  withProviderDirectory,
  withMessages,
  withReports,
  getParticipantLabel,
  isParticipantSelected,
  isAuthenticated,
  title: passedTitle,
  pathname,
}: NavigationContext & {
  isAuthenticated: boolean;
  title?: string;
  pathname?: string;
}): string => {
  if (passedTitle) return passedTitle;

  const map = isAuthenticated
    ? (getNavigationMap({
        roles,
        groups,
        isSelectedPPT,
        showCareProvidersForPPT,
        withProviderRegistry,
        withProviderDirectory,
        withMessages,
        withReports,
        getParticipantLabel,
        isParticipantSelected,
      }).flat() as NavigationRoute[])
    : (unauthorizedNavigationMap as NavigationRoute[]);

  const match = map.find((item) => item && "route" in item && item.route === pathname);
  if (!match) return "";

  return typeof match.title === "function" ? match.title({}) : (match.title ?? "");
};
